import baseTagService from '@Tags/services/baseTag.service';
import { ForgeInspection } from '@/scripts/js/forgeModels';
import { log, warn, error } from '@/utils/debugLogger';

// Category icons — kept client-side (backend shouldn't know about MDI icons)
const CATEGORY_ICONS = {
  'General': 'mdi-information-outline',
  'CML Information': 'mdi-clipboard-text-outline',
  'Inspection & Condition': 'mdi-gauge',
  'Reports': 'mdi-file-document-outline',
  'Equipment & Pipeline': 'mdi-pipe',
  'Risk Assessment': 'mdi-alert-circle-outline',
  'Regulatory & Codes': 'mdi-shield-check-outline',
  'Cathodic Protection': 'mdi-lightning-bolt',
  'Specifications': 'mdi-cog-outline',
};

// Properties already handled by existing fixed filters — exclude from registry
const EXCLUDED_PROPERTIES = new Set(['name', 'friendlyId', 'status']);

// Date fields from model — used for badge display
const DATE_FIELDS = new Set(ForgeInspection.DATE_FIELDS);

// Cached API result
let _cachedRegistry = null;
let _fetchPromise = null;

export const FILTER_THRESHOLD = 50; // Below this = dropdown, above = textbox

/**
 * Load the filter registry from the API (cached after first call)
 * @returns {Promise<Array>} Array of { key, label, category, type, isDate }
 */
export async function loadFilterRegistry() {
  if (_cachedRegistry) return _cachedRegistry;
  if (_fetchPromise) return _fetchPromise;

  _fetchPromise = baseTagService.getFilterableProperties()
    .then(res => {
      _cachedRegistry = (res.data || [])
        .filter(p => !EXCLUDED_PROPERTIES.has(p.key))
        .map(p => ({
          key: p.key,
          label: p.label,
          category: p.category,
          type: p.type,
          // AVA-450: scope groups the catalog into dialog tabs (Inspection / Master /
          // Equipment / Pipeline / Structural). Older payloads have no scope → Inspection.
          scope: p.scope || 'Inspection',
          isDate: p.type === 'date' || DATE_FIELDS.has(p.key),
        }));
      return _cachedRegistry;
    })
    .catch(err => {
      error('Failed to load filterable properties', err);
      _fetchPromise = null;
      return [];
    });

  return _fetchPromise;
}

/**
 * Get the cached registry (synchronous — returns empty array if not yet loaded)
 */
export function getRegistrySync() {
  return _cachedRegistry || [];
}

/**
 * Get registry entries grouped by category
 * @param {string[]} excludeKeys - Property keys to exclude (already added filters)
 * @returns {Object} Grouped filter options (from cache; call loadFilterRegistry first)
 */
export function getGroupedFilterOptions(excludeKeys = []) {
  const registry = _cachedRegistry || [];
  const excludeSet = new Set(excludeKeys);
  const grouped = {};

  for (const entry of registry) {
    if (excludeSet.has(entry.key)) continue;

    const cat = entry.category;
    if (!grouped[cat]) {
      grouped[cat] = {
        label: cat,
        icon: CATEGORY_ICONS[cat] || 'mdi-tag-outline',
        items: [],
      };
    }
    grouped[cat].items.push(entry);
  }

  return grouped;
}

/**
 * Get registry entries grouped by scope, then by category within each scope.
 * Powers the scope tabs in the Add Custom Filters dialog (AVA-450).
 * @param {string[]} excludeKeys - Property keys to exclude (already added filters)
 * @returns {Object} { [scope]: { label, count, groups: { [category]: { label, icon, items } } } }
 */
export function getScopedGroupedFilterOptions(excludeKeys = []) {
  const registry = _cachedRegistry || [];
  const excludeSet = new Set(excludeKeys);
  const scopes = {};

  for (const entry of registry) {
    if (excludeSet.has(entry.key)) continue;

    const scope = entry.scope || 'Inspection';
    if (!scopes[scope]) {
      scopes[scope] = { label: scope, count: 0, groups: {} };
    }

    const cat = entry.category;
    if (!scopes[scope].groups[cat]) {
      scopes[scope].groups[cat] = {
        label: cat,
        icon: CATEGORY_ICONS[cat] || 'mdi-tag-outline',
        items: [],
      };
    }
    scopes[scope].groups[cat].items.push(entry);
    scopes[scope].count++;
  }

  return scopes;
}

/**
 * Get the label for a property key
 */
export function getPropertyLabel(key) {
  if (_cachedRegistry) {
    const entry = _cachedRegistry.find(e => e.key === key);
    if (entry) return entry.label;
    // Handle casing variants (e.g. cLNo1 vs clNo1 from different serializers)
    const ciEntry = _cachedRegistry.find(e => e.key.toLowerCase() === key.toLowerCase());
    if (ciEntry) return ciEntry.label;
    warn(`[FilterRegistry] No registry entry for key "${key}". Falling back to PROPERTY_TITLES.`);
  } else {
    log(`[FilterRegistry] Registry not loaded yet for key "${key}". Using fallback.`);
  }
  const fallback = ForgeInspection.PROPERTY_TITLES[key];
  if (!fallback) {
    warn(`[FilterRegistry] No label found for "${key}" in registry or PROPERTY_TITLES. Returning raw key.`);
  }
  return fallback || key;
}

/**
 * Get the filter type for a property key ('text' | 'date' | 'numeric')
 */
export function getPropertyType(key) {
  if (_cachedRegistry) {
    const entry = _cachedRegistry.find(e => e.key === key);
    if (entry) {
      if (entry.isDate) return 'date';
      return entry.type || 'text';
    }
  }
  if (DATE_FIELDS.has(key)) return 'date';
  return 'text';
}

/**
 * Check whether a custom filter has any active criteria
 */
export function isCustomFilterActive(f) {
  return (f.values && f.values.length > 0) || !!f.searchText ||
         !!f.dateFrom || !!f.dateTo || f.min != null || f.max != null;
}

/**
 * Clear the cache (useful for testing or forced refresh)
 */
export function clearFilterRegistryCache() {
  _cachedRegistry = null;
  _fetchPromise = null;
}
